'use client'
import React, { useEffect, useRef, useState } from 'react'
import { noto_sans, noto_serif, ubuntu } from '@/globalConstants';
import { MenuItem, Skeleton, } from '@mui/material';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { Festival, Months } from '@/globalTypes';
import { Dropdown } from '@mui/base/Dropdown';
import { Menu } from '@mui/base/Menu';
import BreadCrumb from '@/components/BreadCrumb';
import { MenuButton, AnimatedListbox } from '@/lib/globalHooks';
import PageBanner from '../PageBanner';
import { useFestivalContext } from '@/contexts/FestivalContext';
import { smoothScrollToMonth } from '@/lib/scrollToMonth';
import { festivalSlug, istDateParts } from '@/lib/globalUtils';
import Link from 'next/link';

const FestivalsHome = ({ initialMonths, initialYear }: { initialMonths?: Months, initialYear?: string }) => {
    const BASE_CLASS = 'festivals';
    const pathname = usePathname();
    const [dropdown, setDropdown] = useState(false);
    let { selectedYear, handleYearChange, months: fetchedMonths, updatedSelectedFestival, fetchAllFestivals, location, loader } = useFestivalContext();
    // Until the browser's own fetch for a month lands, show the server's copy of
    // it, so the server HTML carries the list and the page doesn't flash back to
    // skeletons while the visitor's location is fetched.
    const months: Months = Object.fromEntries(Object.keys(fetchedMonths).map((month) => {
        const fetched = fetchedMonths[month];
        const seed = selectedYear === initialYear ? initialMonths?.[month] : undefined;
        return [month, fetched === null || fetched.length ? fetched : (seed ?? fetched)];
    }));
    useEffect(() => {
        if (location.city !== '' && months['JANUARY'].length === 0) {
            fetchAllFestivals();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // On first open (current year), smoothly scroll to the current month's
    // section — all 12 months render, so the current month is otherwise buried.
    // Wait for `loader` to clear (every month resolved → stable layout) before
    // scrolling, otherwise months still loading above would shift the target.
    const didAutoScrollRef = useRef(false);
    useEffect(() => {
        if (didAutoScrollRef.current) return;
        if (loader) return;
        if (Number(selectedYear) !== new Date().getFullYear()) return;
        if (!document.getElementById(`festival-month-${new Date().getMonth()}`)) return;
        didAutoScrollRef.current = true;
        smoothScrollToMonth(`festival-month-${new Date().getMonth()}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loader, selectedYear])


    return (
        <>
            <div className={`${BASE_CLASS} w-full`}>
                <PageBanner
                    title="Festivals"
                    subtitle="Select a festival to view its details"
                    breadcrumb={<BreadCrumb pathname={pathname} />}
                />
                <div className='mb-20'></div>
                {
                    <section className={`${BASE_CLASS}`}>
                        <div className={`${BASE_CLASS}__festivals pt-[1rem] pb-[4rem] flex justify-center `}>
                            <div className='panchanga-container w-[95%] md:w-[90%] 2xl:w-[70%]'>
                                <div className='flex justify-center md:justify-center mb-4'>
                                    <div className='heading-and-location__location]'>
                                        <Dropdown open={dropdown}>
                                            <MenuButton className=' text-black bg-white rounded-[0.5rem] items-baseline max-w-[750px]'>
                                                <div className='flex justify-between  gap-5  ' onClick={() => setDropdown(!dropdown)}>
                                                    <Image src={`/calendar-orange.svg`} alt="calendar icon" width={25} height={25} />
                                                    <span className='text-[#EE9322]'>{selectedYear}</span>
                                                    <Image src={`${process.env.NEXT_PUBLIC_ASSETS_BASE_URL}/arrow-up.svg`} alt="" width={15} height={7.5} className='menu-arrow rotate-180'></Image>
                                                </div>
                                            </MenuButton>
                                            <Menu slots={{ listbox: AnimatedListbox }} >
                                                {
                                                    [2024, 2025, 2026, 2027].splice(0, 5).map((item) => {
                                                        return <MenuItem key={item} onClick={() => {
                                                            handleYearChange(item)
                                                            setDropdown(false);
                                                        }} >
                                                            <div className=' flex gap-[1rem] p-[12px]'>
                                                                {item}
                                                            </div>
                                                        </MenuItem>
                                                    })
                                                }
                                            </Menu>
                                        </Dropdown>
                                    </div>
                                </div>
                                <div className={`${BASE_CLASS}__shubh-karya grid p-[0.5rem] md:p-0 gap-[0.75rem] md:gap-[1.25rem] lg:gap-[1.5rem] ${ubuntu.className}`}>
                                    {
                                        Object.keys(months).map((month: string, index: number) => {
                                            return <div key={month} id={`festival-month-${index}`} className={`panchanga__details__tithi p-4 grid gap-[1rem] rounded-[8px] scroll-mt-4`}>
                                                <div className='details__tithi__header flex text-[22px] font-[500] text-[#3C3838] justify-center gap-2'>
                                                    <Image src="/union.svg" alt="union icon" width="30" height="6" />
                                                    {month} {selectedYear}
                                                    <Image src="/union.svg" alt="union icon" width="30" height="6" className='rotate-180  ' />
                                                </div>
                                                {months[month] === null ? <div className='w-full grid justify-center text-[#ee9322]'>No Festivals/Observances today for this month</div> :
                                                    <div className='details__tithi__description grid grid-cols-1 lg:grid-cols-3 gap-[1rem]'>
                                                        {months[month].length === 0 ?
                                                            [1, 2, 3].map((n) => {
                                                                return <Skeleton key={`festival-month-skeleton-${n}`} variant='rounded' animation="wave" />
                                                            })
                                                            : months[month].map((festival: Festival, festivalIndex: number) => {

                                                                const festivalDay = istDateParts(festival.date);
                                                                if (index === festivalDay.month) {
                                                                    // A real link, so crawlers can follow it to the festival page.
                                                                    // A click still goes through the context, which carries this
                                                                    // occurrence's date over to the detail page.
                                                                    return (
                                                                        <Link
                                                                            key={`${festival.date}-${festivalIndex}`}
                                                                            href={`/festivals/${festivalSlug(festival.data)}`}
                                                                            className={`festival__container relative rounded-[12px] border-[1px] border-[#CAC4D0] flex flex-col gap-[0.75rem] p-[0.9rem] lg:p-[1rem] bg-white hover:bg-[#FFEEBB] transition-colors duration-200 shadow-sm w-full cursor-pointer no-underline`}
                                                                            onClick={(event) => { event.preventDefault(); updatedSelectedFestival(festival) }} >
                                                                            <div className="festival-header flex justify-between items-start">
                                                                                <div className="festival-date grid text-left">
                                                                                    <span className={`month text-[#8F8B8B] ${ubuntu.className} font-[500] text-[12px] leading-[14px] uppercase`}>{month.split("").splice(0, 3).join("")}</span>
                                                                                    <span className={`date text-[#3C3838] ${ubuntu.className} font-[600] text-[22px] text-[#EE9322] leading-[26px]`}>{festivalDay.date}</span>
                                                                                </div>
                                                                                <div className="w-[20px] h-[20px] flex items-center justify-center">
                                                                                    <Image src="/arrow-orange.svg" alt="Go to festival" width={20} height={20} />
                                                                                </div>
                                                                            </div>

                                                                            <div className="festival-name flex items-start gap-[6px]">
                                                                                <span className={`${ubuntu.className} font-[600] text-[16px] leading-[22px] text-[#1D4749]`}>{festival.data.name || festival.data.observance_name}</span>
                                                                            </div>

                                                                            <div className={`festival-description text-[#565151] text-[13px] lg:text-[14px] leading-[20px] ${noto_sans.className}`}>
                                                                                {festival.data.description_1}
                                                                            </div>
                                                                        </Link>
                                                                    )
                                                                }
                                                            })
                                                        }
                                                    </div>
                                                }
                                            </div>
                                        })

                                    }
                                </div>
                            </div>
                        </div>
                    </section>
                }

            </div>
        </>
    )
}

export default FestivalsHome

